import { useEffect, useState } from 'react'
import { apiGet } from '../../../../../generic/Api_Functions'
import { toComponentOptions } from '../utils/componentFilter'

// Story 219 (CU6, P1/P2): the catalog of components, `GET mos/components` (`[{ code, name }]`,
// contract §2). Asked once and shared by every surface of the screen -- the widget's header, the
// "Show all" modal from either opener and Operation's prefetch. A failed request is forgotten, so the
// next surface that needs the catalog asks again (P1). `async` so that even a synchronous throw ends up
// as a rejection, which every caller already treats as "no options".
const requestComponents = async () => apiGet('mos/components')

const NOT_LOADED = { loaded: false, options: [] }

// O1 amendment (RN15.1): the catalog is ONE for the screen. Whoever gets it -- the modal on its retry,
// Operation's prefetch -- publishes it to every mounted surface, so a header whose own request failed
// gets the options without asking again and without remounting.
let sharedCatalog = NOT_LOADED
const subscribers = new Set()

const publish = (raw) => {
  sharedCatalog = { loaded: true, options: toComponentOptions(raw) }
  subscribers.forEach((notify) => notify(sharedCatalog))
}

let componentsPromise = null
export const prefetchPlannedJobComponents = () => {
  if (!componentsPromise) {
    componentsPromise = requestComponents().then((raw) => {
      publish(raw)
      return raw
    }, (err) => {
      componentsPromise = null
      throw err
    })
  }
  return componentsPromise
}

/**
 * The options of the "Component" field. Nothing is asked until `enabled`; a failure leaves the field
 * without options and silent (P1), and is retried the next time the caller becomes enabled again
 * (the modal reopening) or mounts again (the widget). Whatever any surface loads reaches this one too
 * (RN15.1).
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true]
 * @returns {{code: string, label: string}[]}
 */
const usePlannedJobComponents = ({ enabled = true } = {}) => {
  const [catalog, setCatalog] = useState(sharedCatalog)

  useEffect(() => {
    subscribers.add(setCatalog)
    // It may have arrived between the first render and this subscription.
    setCatalog(sharedCatalog)
    return () => { subscribers.delete(setCatalog) }
  }, [])

  useEffect(() => {
    if (!enabled || catalog.loaded) return
    prefetchPlannedJobComponents().catch(() => {})
  }, [enabled, catalog.loaded])

  return catalog.options
}

export default usePlannedJobComponents
